<template>
  <div class="chart-wrapper">
    <Line 
      v-if="built"
      :data="chartData"
      :options="chartOptions"
    />
  </div>
</template>

<script>
import Moment from 'moment';
import { Line } from 'vue-chartjs';
import { Chart as ChartJS, LineElement, PointElement, TimeScale, LinearScale, Tooltip } from 'chart.js';
import 'chartjs-adapter-moment';
import VLcurrency from "@/composables/VLcurrency"
import VLpercent from "@/composables/VLpercent"
import ColorsMixin from "@/mixins/ColorsMixin";

ChartJS.register(LineElement, PointElement, TimeScale, LinearScale, Tooltip);

export default {
    name: "LineChart",

    components: {
        Line,
    },

    mixins: [ ColorsMixin ],

    props: {
        dates: {
            type: Object,
            required: true,
        },
        client: {
            type: Object,
            required: true,
        },
        tiers: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            built: false,
            chartData: {},

            chartOptions: {
                clip: false,
                animation: false,
                maintainAspectRatio: false,
                devicePixelRatio: 4, // prevents blurry charts
                responsive: true,
                plugins: {
                    legend: {
                        display: false,
                    },
                    title: {
                        display: false,
                    },
                    tooltip: {
                        displayColors: false,
                        callbacks: {},
                    }
                },
                elements: {
                    point: {
                        radius: 6,
                        hoverRadius: 7,
                    },
                    line: {
                        borderDash: [10, 5],
                        borderColor: '#000',
                    }
                },
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            unit: 'quarter',
                            displayFormats: {
                                quarter: 'QQQ'
                            },
                            tooltipFormat: 'MMM DD, yyyy',
                        },
                        border: {
                            display: false,
                        },
                        grid: {
                            display: false,
                            drawOnChartArea: true,
                            drawTicks: true,
                        },
                        ticks: {
                            callback: function(val, index) {
                                if (index == 0) {
                                    return 'START';
                                } else if (index == 4) {
                                    return 'END OF YEAR';
                                } else {
                                    return 'Q' + index;
                                }
                            },
                            color: '#1F3A4D',
                            font: {
                                size: 15,
                            },
                        },
                    },
                    y: {
                        min: -1,
                        max: 7,
                        border: {
                            display: false
                        },
                        grid: {
                            lineWidth: 20,
                            color: '#CDD1D2',
                        },
                        ticks: {
                            padding: 30,
                            color: '#1F3A4D',
                            font: {
                                size: 15
                            },
                        },
                        position: 'right',
                    },
                },
            },
        }
    },

    computed: {
        clientStyles() {
            if (this.$dataDirectory?.userData?.client_styles) return JSON.parse(this.$dataDirectory.userData.client_styles)
            else return null
        },
        planYear() {
            return Moment(this.dates.start, 'YYYY-MM-DD').toDate().getFullYear()
        },
        pointColor() {
          return this.getHexColor('highlight')
        },
    },

    watch: {
        clientStyles: {
            handler: function () {
                this.buildChartData()
            },
            deep: true
        }
    },

    mounted() {
        this.buildChartOptions()
        this.buildChartData()
    },

    methods: {
        buildChartOptions() {
            const history = this.client.performance.history;
            const mode = this.client.performance.mode;
            const hideResults = this.client.hide_client_results;
            this.chartOptions.plugins.tooltip.callbacks.label = function(context) {
                if (context.dataIndex > 0) {
                    if (hideResults) {
                        return '';
                    }
                    else if (mode == 'profit') {
                        return `Profit: ${VLcurrency.formatCurrency(history[context.dataIndex - 1].profit)}`
                    }
                    return [
                        `Revenue: ${VLcurrency.formatCurrency(history[context.dataIndex - 1].revenue)}`,
                        `Margin: ${VLpercent.formatPercent(history[context.dataIndex - 1].margin, 2)}`,
                        `Profit: ${VLcurrency.formatCurrency(history[context.dataIndex - 1].profit)}`,
                    ];
                } else {
                    return 'Plan Start';
                }
            };

            const adjustedEndDate = new Date(this.dates.end);
            adjustedEndDate.setDate(adjustedEndDate.getDate() + 2);
            this.chartOptions.scales.x.min = this.dates.start;
            this.chartOptions.scales.x.max = adjustedEndDate;

            const baseLabel = this.tiers.base.label[0].toUpperCase() + this.tiers.base.label.slice(1);
            const targetLabel = this.tiers.target.label[0].toUpperCase() + this.tiers.target.label.slice(1);
            const superiorLabel = this.tiers.superior.label[0].toUpperCase() + this.tiers.superior.label.slice(1);
            const axisLabels = [
              `Below ${baseLabel}`,
              baseLabel,
              `Between ${baseLabel} and ${targetLabel}`,
              targetLabel,
              `Between ${targetLabel} and ${superiorLabel}`,
              superiorLabel,
              `Above ${superiorLabel}`
            ];
            this.chartOptions.scales.y.ticks.callback = function (value) {
                if (value % 2 == 0) return null;
                return axisLabels[value];
            };
            this.chartOptions.elements.point.backgroundColor = this.pointColor;
        },

        buildChartData() {
            let start_date = Moment(this.dates.start, 'YYYY-MM-DD').toDate()
            this.chartData = {
                datasets: [
                    {
                        data: [
                            [start_date, 3],
                        ],
                    },
                ],
            };

            this.client.performance.history.forEach((item) => {
                let date = Moment(item.date, 'YYYY-MM-DD').toDate()

                this.chartData.datasets[0].data.push([date, item.zone - 1])
            })
            this.built = true;
        },
    },
}
</script>

<style lang="scss" scoped>
@use "../../../../scss/variables";
.chart-wrapper {
    width: 100%;
    height: 300px;
}
</style>